import { PDFDocument, rgb, StandardFonts, degrees } from 'pdf-lib';
import { formatCurrency } from './currencyService';

export class PDFService {
  static async populateLoanAgreement(loanData, borrowerData, nextOfKinData) {
    try {
      // Load the existing PDF
      const existingPdfBytes = await fetch('/LOAN AGREEMENT 2025.pdf').then(res => res.arrayBuffer());
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      
      // Create a new PDF
      const newPdfDoc = await PDFDocument.create();
      
      // Embed fonts
      const helveticaFont = await newPdfDoc.embedFont(StandardFonts.Helvetica);
      const helveticaBoldFont = await newPdfDoc.embedFont(StandardFonts.HelveticaBold);
      
      // Try embed watermark image (centered on each page)
      let watermarkImage = null;
      try {
        const wmBytes = await fetch('/logo-watermark.png').then(res => res.arrayBuffer());
        // Support PNG; if file is JPEG this will throw and be caught below
        try {
          watermarkImage = await newPdfDoc.embedPng(wmBytes);
        } catch (e) {
          watermarkImage = await newPdfDoc.embedJpg(wmBytes);
        }
      } catch (e) {
        // If watermark fails to load, proceed without blocking PDF generation
        console.warn('Watermark image not embedded:', e);
      }
      
      // Copy all pages from the original
      const pages = await newPdfDoc.copyPages(pdfDoc, pdfDoc.getPageIndices());
      pages.forEach((page) => newPdfDoc.addPage(page));
      
      // Get pages
      const firstPage = newPdfDoc.getPages()[0];
      const secondPage = newPdfDoc.getPages()[1];
      const thirdPage = newPdfDoc.getPages()[2];
      
      // Draw watermark on every page (on top, low opacity)
      if (watermarkImage) {
        newPdfDoc.getPages().forEach((page) => {
          const { width, height } = page.getSize();
          // Target watermark size relative to page width
          const targetWidth = Math.min(320, width * 0.45);
          const aspect = watermarkImage.height / watermarkImage.width;
          const targetHeight = targetWidth * aspect;
          const x = (width - targetWidth) / 2;
          const y = (height - targetHeight) / 2;
          page.drawImage(watermarkImage, {
            x,
            y,
            width: targetWidth,
            height: targetHeight,
            rotate: degrees(-45),
            opacity: 0.08,
          });
        });
      }
      
      // Set font and size
      const fontSize = 10;
      const fontColor = rgb(0, 0, 0);

      // Helper function to draw text with better positioning
      const drawText = (page, text, opts) => {
        const {
          x = 50,
          y = 750,
          align = 'left',
          size = fontSize,
          color = fontColor,
          bold = false,
          debug = false // Set to true to see positioning boxes
        } = opts || {};

        const font = bold ? helveticaBoldFont : helveticaFont;
        let drawX = x;
        
        // Handle text alignment
        if (align !== 'left') {
          const width = font.widthOfTextAtSize(String(text || ''), size);
          if (align === 'center') drawX = x - width / 2;
          if (align === 'right') drawX = x - width;
        }
        
        // Draw the text
        page.drawText(String(text || ''), {
          x: drawX,
          y: y,
          size,
          color,
          font,
        });
        
        // Draw debug rectangle if enabled
        if (debug) {
          const textWidth = font.widthOfTextAtSize(String(text || ''), size);
          const textHeight = size;
          page.drawRectangle({
            x: drawX,
            y: y - textHeight / 2,
            width: textWidth,
            height: textHeight,
            borderColor: rgb(1, 0, 0),
            borderWidth: 1,
          });
        }
      };

      // Page 1 - Cover page
      if (borrowerData?.names) {
        drawText(firstPage, borrowerData.names.toUpperCase(), {
          x: 297, y: 165, align: 'center', size: 11, bold: true
        });
      }
      
      // Page 2 - Borrower details
      if (borrowerData) {
        // Borrower details - left column
        drawText(secondPage, borrowerData.names || '_________', {
          x: 156, y: 520, size: 9
        });
        
        drawText(secondPage, borrowerData.nrc || '_________', {
          x: 184, y: 502, size: 9
        });
        
        drawText(secondPage, borrowerData.occupation || '_________', {
          x: 171, y: 484, size: 9
        });
        
        drawText(secondPage, borrowerData.address || '_________', {
          x: 161, y: 466, size: 9
        });
        
        drawText(secondPage, borrowerData.email || '_________', {
          x: 183, y: 448, size: 9
        });
        
        drawText(secondPage, borrowerData.phone || '_________', {
          x: 156, y: 430, size: 9
        });
      }
      
      // Next of Kin details - right column
      if (nextOfKinData) {
        drawText(secondPage, nextOfKinData.names || '_________', {
          x: 486, y: 520, size: 9
        });
        
        drawText(secondPage, nextOfKinData.nrc || '_________', {
          x: 514, y: 502, size: 9
        });
        
        drawText(secondPage, nextOfKinData.occupation || '_________', {
          x: 501, y: 484, size: 9
        });
        
        drawText(secondPage, nextOfKinData.address || '_________', {
          x: 491, y: 466, size: 9
        });
        
        drawText(secondPage, nextOfKinData.phone || '_________', {
          x: 486, y: 448, size: 9
        });
        
        drawText(secondPage, nextOfKinData.relationship || '_________', {
          x: 503, y: 430, size: 9
        });
      }
      
      // Loan Terms section
      if (loanData) {
        drawText(secondPage, loanData.agreement_date || new Date().toLocaleDateString('en-GB'), {
          x: 207, y: 364, size: 9
        });
        
        drawText(secondPage, loanData.repayment_day || '_________', {
          x: 202, y: 346, size: 9
        });
        
        drawText(secondPage, loanData.loan_period || '_________', {
          x: 177, y: 328, size: 9
        });
        
        drawText(secondPage, loanData.collateral || '_________', {
          x: 467, y: 328, size: 9
        });
        
        // Disbursement Account - handle long text
        const accountText = loanData.disbursement_account || '_________';
        if (accountText.length > 45) {
          const line1 = accountText.substring(0, 45);
          const line2 = accountText.substring(45);
          drawText(secondPage, line1, { x: 172, y: 298, size: 9 });
          drawText(secondPage, line2, { x: 172, y: 286, size: 9 });
        } else {
          drawText(secondPage, accountText, {
            x: 172, y: 292, size: 9
          });
        }
      }
      
      // Page 3 - Fee breakdown
      if (loanData) {
        const principal = parseFloat(loanData.principal_amount || 0);
        
        // Principal Amount
        drawText(thirdPage, formatCurrency(principal), {
          x: 518, y: 655, size: 9, align: 'right'
        });
        
        // Interest (4%)
        const interest = principal * 0.04;
        drawText(thirdPage, formatCurrency(interest), {
          x: 518, y: 635, size: 9, align: 'right'
        });
        
        // Processing Fee (6%)
        const processingFee = principal * 0.06;
        drawText(thirdPage, formatCurrency(processingFee), {
          x: 518, y: 615, size: 9, align: 'right'
        });
        
        // Service Fee (5%)
        const serviceFee = principal * 0.05;
        drawText(thirdPage, formatCurrency(serviceFee), {
          x: 518, y: 595, size: 9, align: 'right'
        });
        
        // Underwriting Fee (5%)
        const underwritingFee = principal * 0.05;
        drawText(thirdPage, formatCurrency(underwritingFee), {
          x: 518, y: 575, size: 9, align: 'right'
        });
        
        // Admin Fee (8%)
        const adminFee = principal * 0.08;
        drawText(thirdPage, formatCurrency(adminFee), {
          x: 518, y: 555, size: 9, align: 'right'
        });
        
        // Total Fees (24%) — excludes interest
        const totalFees = principal * 0.24;
        drawText(thirdPage, formatCurrency(totalFees), {
          x: 518, y: 535, size: 9, align: 'right'
        });
        
        // Total Amount = Principal + Interest + Fees
        const totalAmount = principal + interest + totalFees;
        drawText(thirdPage, formatCurrency(totalAmount), {
          x: 518, y: 515, size: 9, align: 'right', bold: true
        });
        
        // Additional charges if applicable
        if (loanData.endorsement_fee && parseFloat(loanData.endorsement_fee) > 0) {
          drawText(thirdPage, formatCurrency(loanData.endorsement_fee), {
            x: 518, y: 475, size: 9, align: 'right'
          });
        }
        
        if (loanData.vehicle_tracking_fee && parseFloat(loanData.vehicle_tracking_fee) > 0) {
          drawText(thirdPage, formatCurrency(loanData.vehicle_tracking_fee), {
            x: 518, y: 455, size: 9, align: 'right'
          });
        }
        
        // Total amount to be repaid = Total Amount + optional extra fees
        const totalRepayment = totalAmount + 
          (parseFloat(loanData.endorsement_fee || 0)) + 
          (parseFloat(loanData.vehicle_tracking_fee || 0));

        drawText(thirdPage, formatCurrency(totalRepayment), {
          x: 518, y: 415, size: 9, align: 'right', bold: true
        });
      }
      
      // Generate PDF bytes
      const pdfBytes = await newPdfDoc.save();
      
      return pdfBytes;
    } catch (error) {
      console.error('Error populating PDF:', error);
      throw new Error('Failed to populate PDF: ' + error.message);
    }
  }

  // Helper method to determine coordinates interactively
  static async debugPDFCoordinates(loanData, borrowerData, nextOfKinData) {
    const pdfBytes = await this.populateLoanAgreement(loanData, borrowerData, nextOfKinData);
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    
    // Open in new window for inspection
    window.open(url, '_blank');
    
    return "Debug mode activated. Check the generated PDF for positioning.";
  }
  
  static async downloadPopulatedPDF(loanData, borrowerData, nextOfKinData, filename = 'loan_agreement.pdf') {
    try {
      const pdfBytes = await this.populateLoanAgreement(loanData, borrowerData, nextOfKinData);
      
      // Create blob and download
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the object URL
      URL.revokeObjectURL(url);
      
      return true;
    } catch (error) {
      console.error('Error downloading PDF:', error);
      throw error;
    }
  }
  
  static async populateLoanAgreementFromHTML(loanData, borrowerData, nextOfKinData, filename = 'loan_agreement.pdf') {
    try {
      // Load the HTML template
      const htmlResponse = await fetch('/loan_agreement_template.html');
      const htmlTemplate = await htmlResponse.text();
      
      // Ensure watermark image is available inside blob window by inlining it
      let watermarkDataUrl = null;
      try {
        const wmResp = await fetch('/logo-watermark.png');
        const wmBlob = await wmResp.blob();
        watermarkDataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(wmBlob);
        });
      } catch (e) {
        console.warn('Unable to inline watermark; falling back to path.', e);
      }
      
      // Replace placeholders in HTML template
      let populatedHTML = htmlTemplate;
      // Swap watermark src to a data URL to avoid blob-origin path issues
      if (watermarkDataUrl) {
        populatedHTML = populatedHTML.replace(/src=["']\/logo-watermark\.png["']/g, `src="${watermarkDataUrl}"`);
      } else {
        // As a fallback, inject absolute origin so `/...` resolves even from blob
        populatedHTML = populatedHTML.replace(/src=["']\/logo-watermark\.png["']/g, `src="${window.location.origin}/logo-watermark.png"`);
      }
      
      // Replace borrower information placeholders
      populatedHTML = populatedHTML
        .replace(/{{borrower_name}}/g, borrowerData?.names || '_________')
        .replace(/{{borrower_nrc}}/g, borrowerData?.nrc || '_________')
        .replace(/{{borrower_occupation}}/g, borrowerData?.occupation || '_________')
        .replace(/{{borrower_address}}/g, borrowerData?.address || '_________')
        .replace(/{{borrower_email}}/g, borrowerData?.email || '_________')
        .replace(/{{borrower_phone}}/g, borrowerData?.phone || '_________');
      
      // Replace next of kin information placeholders
      populatedHTML = populatedHTML
        .replace(/{{next_of_kin_name}}/g, nextOfKinData?.names || '_________')
        .replace(/{{next_of_kin_nrc}}/g, nextOfKinData?.nrc || '_________')
        .replace(/{{next_of_kin_occupation}}/g, nextOfKinData?.occupation || '_________')
        .replace(/{{next_of_kin_address}}/g, nextOfKinData?.address || '_________')
        .replace(/{{next_of_kin_phone}}/g, nextOfKinData?.phone || '_________')
        .replace(/{{next_of_kin_relationship}}/g, nextOfKinData?.relationship || '_________');
      
      // Replace loan information placeholders
      const principal = parseFloat(loanData?.principal_amount || 0);
      const interest = principal * 0.04;
      const processingFee = principal * 0.06;
      const serviceFee = principal * 0.05;
      const underwritingFee = principal * 0.05;
      const adminFee = principal * 0.08;
      const totalFees = principal * 0.24; // excludes interest
      const totalAmount = principal + interest + totalFees; // include interest
      const totalRepayment = totalAmount + 
        (parseFloat(loanData?.endorsement_fee || 0)) + 
        (parseFloat(loanData?.vehicle_tracking_fee || 0));
      
      populatedHTML = populatedHTML
        .replace(/{{agreement_date}}/g, loanData?.agreement_date || new Date().toLocaleDateString('en-GB'))
        .replace(/{{repayment_day}}/g, loanData?.repayment_day || '_________')
        .replace(/{{loan_period}}/g, loanData?.loan_period || '_________')
        .replace(/{{collateral}}/g, loanData?.collateral || '_________')
        .replace(/{{disbursement_account}}/g, loanData?.disbursement_account || '_________')
        .replace(/{{principal_amount}}/g, principal > 0 ? formatCurrency(principal) : '_________')
        .replace(/{{interest_amount}}/g, principal > 0 ? formatCurrency(interest) : '_________')
        .replace(/{{processing_fee}}/g, principal > 0 ? formatCurrency(processingFee) : '_________')
        .replace(/{{service_fee}}/g, principal > 0 ? formatCurrency(serviceFee) : '_________')
        .replace(/{{underwriting_fee}}/g, principal > 0 ? formatCurrency(underwritingFee) : '_________')
        .replace(/{{admin_fee}}/g, principal > 0 ? formatCurrency(adminFee) : '_________')
        .replace(/{{total_fees}}/g, principal > 0 ? formatCurrency(totalFees) : '_________')
        .replace(/{{total_amount}}/g, principal > 0 ? formatCurrency(totalAmount) : '_________')
        .replace(/{{endorsement_fee}}/g, (loanData?.endorsement_fee && parseFloat(loanData.endorsement_fee) > 0) ? formatCurrency(loanData.endorsement_fee) : '_________')
        .replace(/{{vehicle_tracking_fee}}/g, (loanData?.vehicle_tracking_fee && parseFloat(loanData.vehicle_tracking_fee) > 0) ? formatCurrency(loanData.vehicle_tracking_fee) : '_________')
        .replace(/{{total_repayment}}/g, principal > 0 ? formatCurrency(totalRepayment) : '_________');
      
      // Create a blob with the populated HTML
      const borrowerName = borrowerData?.names || 'Borrower';
      const blob = new Blob([`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Loan Agreement - ${borrowerName}</title>
          <style>
            @page {
              size: A4;
              margin: 20mm;
            }
            body {
              font-family: Arial, sans-serif;
              line-height: 1.4;
              margin: 0;
              padding: 0;
            }
            @media print {
              body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
          </style>
          <base href="${window.location.origin}/">
        </head>
        <body>
          ${populatedHTML}
        </body>
        </html>
      `], { type: 'text/html' });
      
      const url = URL.createObjectURL(blob);
      
      // Open in new window for printing
      const printWindow = window.open(url, '_blank');
      
      // Wait for content to load, then trigger print dialog
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        });
      }
      
      // Clean up the object URL after a delay
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 10000);
      
      return true;
    } catch (error) {
      console.error('Error populating HTML template:', error);
      throw new Error('Failed to populate HTML template: ' + error.message);
    }
  }
  
  static async downloadPopulatedHTMLPDF(loanData, borrowerData, nextOfKinData, filename = 'loan_agreement.pdf') {
    try {
      // Use the HTML template approach
      return await this.populateLoanAgreementFromHTML(loanData, borrowerData, nextOfKinData, filename);
    } catch (error) {
      console.error('Error downloading HTML PDF:', error);
      throw error;
    }
  }

  static formatLoanDataForPDF(loan, borrower, nextOfKin = null) {
    const currentDate = new Date();
    
    const loanData = {
      agreement_date: currentDate.toLocaleDateString('en-GB'),
      repayment_day: loan.repayment_date || loan.loan_enddate || currentDate.toLocaleDateString('en-GB'),
      loan_period: `${loan.loan_period || 30} days`,
      collateral: loan.collateral || '_________',
      disbursement_account: loan.disbursement_account || borrower?.phone || '_________',
      principal_amount: loan.loan_amount || loan.amount || 0,
      endorsement_fee: loan.endorsement_fee || 0,
      vehicle_tracking_fee: loan.gps_fee || 0,
    };
    
    const borrowerData = {
      names: `${borrower?.first_name || ''} ${borrower?.last_name || ''}`.trim() || borrower?.borrower_name || borrower?.name || '_________',
      nrc: borrower?.nrc || borrower?.national_id || '_________',
      occupation: borrower?.occupation || borrower?.employer || borrower?.position || '_________',
      address: borrower?.address || '_________',
      // leave email blank if missing to avoid failing form 'email' validator
      email: (borrower?.email || '').trim(),
      phone: borrower?.phone || '_________',
    };
    
    const nextOfKinData = nextOfKin ? {
      names: nextOfKin.names || nextOfKin.name || `${nextOfKin.first_name || ''} ${nextOfKin.last_name || ''}`.trim() || '_________',
      nrc: nextOfKin.nrc || nextOfKin.national_id || '_________',
      occupation: nextOfKin.occupation || '_________',
      address: nextOfKin.address || '_________',
      phone: nextOfKin.phone || '_________',
      relationship: nextOfKin.relationship || '_________',
    } : {
      names: borrower?.next_kin_name || borrower?.next_of_kin_name || '_________',
      nrc: borrower?.next_kin_nrc || borrower?.next_of_kin_nrc || '_________',
      occupation: borrower?.next_kin_occupation || borrower?.next_of_kin_occupation || '_________',
      address: borrower?.next_kin_address || borrower?.next_of_kin_address || '_________',
      phone: borrower?.next_kin_phone || borrower?.next_of_kin_phone || '_________',
      relationship: borrower?.next_kin_relationship || borrower?.next_of_kin_relationship || '_________',
    };
    
    return { loanData, borrowerData, nextOfKinData };
  }

  static async generateLoanStatement(loanData, borrowerData, transactions = [], filename = 'loan_statement.pdf') {
    try {
      // Load the HTML template
      const htmlResponse = await fetch('/loan_statement_template.html');
      const htmlTemplate = await htmlResponse.text();
      
      // Ensure watermark image is available
      let watermarkDataUrl = null;
      try {
        const wmResp = await fetch('/logo-watermark.png');
        const wmBlob = await wmResp.blob();
        watermarkDataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(wmBlob);
        });
      } catch (e) {
        console.warn('Unable to inline watermark for statement.', e);
      }
      
      // Replace placeholders in HTML template
      let populatedHTML = htmlTemplate;
      
      // Replace watermark src
      if (watermarkDataUrl) {
        populatedHTML = populatedHTML.replace(/src=["']\/logo-watermark\.png["']/g, `src="${watermarkDataUrl}"`);
      } else {
        populatedHTML = populatedHTML.replace(/src=["']\/logo-watermark\.png["']/g, `src="${window.location.origin}/logo-watermark.png"`);
      }

      // Format amounts with currency
      const formatAmount = (amount) => {
        if (!amount || parseFloat(amount) === 0) return 'ZMW 0.00';
        return `ZMW ${parseFloat(amount).toLocaleString('en-ZM', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      };

      // Calculate fees and amounts
      const principal = parseFloat(loanData?.principal_amount || loanData?.amount || 0);
      const gpsFeesMonth1 = formatAmount(400);
      const gpsFeesMonth2 = formatAmount(400);
      const interestAndFeesMonth1 = formatAmount(principal * 0.28); // 4% + 24% fees
      const interestAndFeesMonth2 = formatAmount(principal * 0.28);
      const totalAmountPaid = parseFloat(loanData?.paid_amount || 0);
      const totalFeesAndInterest = principal * 0.56 + 800; // 2 months fees + GPS
      const amountDue = principal + totalFeesAndInterest - totalAmountPaid;
      const collateralValue = formatAmount(loanData?.collateral_value || principal * 2.2); // Estimate if not provided

      // Generate transactions rows
      let transactionsRows = '';
      if (transactions && transactions.length > 0) {
        transactionsRows = transactions.map(tx => {
          const date = new Date(tx.transaction_date || tx.date).toLocaleDateString('en-GB');
          const type = tx.transaction_type || tx.type || 'LOAN PAYMENT';
          const method = tx.payment_method || tx.method || 'AIRTEL MONEY';
          const amount = formatAmount(tx.amount);
          const reference = tx.reference || tx.transaction_id || 'N/A';
          const status = tx.status || 'COMPLETED';
          
          return `
            <tr>
              <td>${date}</td>
              <td>${type}</td>
              <td>${method}</td>
              <td class="amount">${amount}</td>
              <td>${reference}</td>
              <td>${status}</td>
            </tr>
          `;
        }).join('');
      } else {
        // Default transaction rows if no transactions provided
        transactionsRows = `
          <tr>
            <td>${new Date(loanData?.disbursement_date || loanData?.approval_date || Date.now()).toLocaleDateString('en-GB')}</td>
            <td>LOAN DISBURSEMENT</td>
            <td>AIRTEL MONEY</td>
            <td class="amount">${formatAmount(principal)}</td>
            <td>${loanData?.loan_id || Math.floor(Math.random() * 1000000000)}</td>
            <td>COMPLETED</td>
          </tr>
        `;
        
        if (totalAmountPaid > 0) {
          transactionsRows += `
            <tr>
              <td>${new Date().toLocaleDateString('en-GB')}</td>
              <td>LOAN REPAYMENT</td>
              <td>AIRTEL MONEY</td>
              <td class="amount">${formatAmount(totalAmountPaid)}</td>
              <td>${Math.floor(Math.random() * 1000000000)}</td>
              <td>COMPLETED</td>
            </tr>
          `;
        }
      }

      // Replace all placeholders
      populatedHTML = populatedHTML
        .replace(/{{borrower_name}}/g, borrowerData?.names?.toUpperCase() || borrowerData?.name?.toUpperCase() || '_________')
        .replace(/{{disbursement_date}}/g, new Date(loanData?.disbursement_date || loanData?.approval_date || Date.now()).toLocaleDateString('en-GB'))
        .replace(/{{borrower_nrc}}/g, borrowerData?.nrc || '_________')
        .replace(/{{disbursement_amount}}/g, formatAmount(principal))
        .replace(/{{borrower_phone}}/g, borrowerData?.phone || '_________')
        .replace(/{{loan_reference}}/g, loanData?.loan_id || loanData?.id || Math.floor(Math.random() * 1000000))
        .replace(/{{collateral_description}}/g, loanData?.collateral_description?.toUpperCase() || loanData?.collateral?.toUpperCase() || 'VEHICLE')
        .replace(/{{account_status}}/g, loanData?.status_name?.toUpperCase() || 'ACTIVE')
        .replace(/{{gps_fee_month1}}/g, gpsFeesMonth1)
        .replace(/{{gps_fee_month2}}/g, gpsFeesMonth2)
        .replace(/{{interest_fees_month1}}/g, interestAndFeesMonth1)
        .replace(/{{interest_fees_month2}}/g, interestAndFeesMonth2)
        .replace(/{{amount_paid}}/g, formatAmount(totalAmountPaid))
        .replace(/{{amount_due}}/g, formatAmount(amountDue))
        .replace(/{{collateral_value}}/g, collateralValue)
        .replace(/{{transactions_rows}}/g, transactionsRows)
        .replace(/{{statement_date}}/g, new Date().toLocaleDateString('en-GB'))
        .replace(/{{issued_by}}/g, loanData?.issued_by?.toUpperCase() || 'LOAN OFFICER');

      // Create a blob with the populated HTML
      const borrowerName = borrowerData?.names || 'Borrower';
      const blob = new Blob([`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Loan Statement - ${borrowerName}</title>
          <style>
            @page {
              size: A4;
              margin: 15mm;
            }
            body {
              font-family: Arial, sans-serif;
              line-height: 1.4;
              margin: 0;
              padding: 0;
            }
            @media print {
              body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
          </style>
          <base href="${window.location.origin}/">
        </head>
        <body>
          ${populatedHTML}
        </body>
        </html>
      `], { type: 'text/html' });
      
      const url = URL.createObjectURL(blob);
      
      // Open in new window for printing
      const printWindow = window.open(url, '_blank');
      
      // Wait for content to load, then trigger print dialog
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        });
      }
      
      // Clean up the object URL after a delay
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 10000);
      
      return true;
    } catch (error) {
      console.error('Error generating loan statement:', error);
      throw new Error('Failed to generate loan statement: ' + error.message);
    }
  }

  static formatLoanDataForStatement(loan, borrower, transactions = [], currentUser = null) {
    // Debug logging
    console.log('Statement Generation - Raw borrower data:', borrower);

    const loanData = {
      principal_amount: loan?.loan_amount || loan?.amount || 0,
      disbursement_date: loan?.approval_date || loan?.disbursement_date || new Date().toISOString(),
      paid_amount: loan?.paid_amount || 0,
      loan_id: loan?.id || loan?.loan_id,
      collateral_description: loan?.collateral_description || loan?.collateral,
      status_name: loan?.status_name || 'Active',
      collateral_value: loan?.collateral_value,
      issued_by: currentUser?.name || currentUser?.first_name && currentUser?.last_name
        ? `${currentUser.first_name} ${currentUser.last_name}`.trim()
        : loan?.issued_by || loan?.loan_officer?.name
    };

    // Try multiple field name patterns for NRC
    const nrc = borrower?.nrc ||
                borrower?.national_id ||
                borrower?.nrc_number ||
                borrower?.id_number ||
                borrower?.nationalId ||
                borrower?.NRC ||
                borrower?.nrcNumber;

    // Try multiple field name patterns for phone
    const phone = borrower?.phone ||
                  borrower?.phone_number ||
                  borrower?.mobile ||
                  borrower?.contact ||
                  borrower?.phoneNumber ||
                  borrower?.mobileNumber ||
                  borrower?.contactNumber ||
                  borrower?.Phone;

    const borrowerData = {
      names: `${borrower?.first_name || ''} ${borrower?.last_name || ''}`.trim() ||
             borrower?.borrower_name ||
             borrower?.name ||
             borrower?.fullName ||
             borrower?.full_name,
      nrc: nrc,
      phone: phone
    };

    console.log('Statement Generation - Formatted borrower data:', borrowerData);

    return { loanData, borrowerData, transactions };
  }
}

export default PDFService;
